import React, { useState, useEffect } from 'react';
import { tokenManager } from '../../utils/tokenManager';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';

interface SessionTimeoutWarningProps {
  warningThreshold?: number; // Minutes before timeout to show warning
  onExtendSession?: () => void;
  onForceLogout?: () => void;
}

export const SessionTimeoutWarning: React.FC<SessionTimeoutWarningProps> = ({
  warningThreshold = 5, // Default 5 minutes warning
  onExtendSession,
  onForceLogout
}) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const checkSessionTimeout = () => {
      const tokenInfo = tokenManager.getTokenInfo();
      if (!tokenInfo) {
        setIsActive(false);
        return;
      }

      setIsActive(true);
      
      const now = Date.now();
      const timeUntilExpiry = tokenInfo.expiresAt - now;
      const warningTime = warningThreshold * 60 * 1000; // Convert to milliseconds
      
      if (timeUntilExpiry <= warningTime && timeUntilExpiry > 0) {
        setShowWarning(true);
        setTimeRemaining(Math.ceil(timeUntilExpiry / 1000 / 60)); // Convert to minutes
      } else if (timeUntilExpiry <= 0) {
        // Session has expired
        setShowWarning(false);
        handleForceLogout();
      } else {
        setShowWarning(false);
      }
    };

    // Check immediately
    checkSessionTimeout();

    // Check every 30 seconds
    const interval = setInterval(checkSessionTimeout, 30000);

    return () => clearInterval(interval);
  }, [warningThreshold]);

  const handleExtendSession = async () => {
    try {
      // Trigger token refresh to extend session
      await tokenManager.getValidToken();
      setShowWarning(false);
      onExtendSession?.();
    } catch (error) {
      console.error('Failed to extend session:', error);
      handleForceLogout();
    }
  };

  const handleForceLogout = () => {
    setShowWarning(false);
    tokenManager.forceSessionTimeout();
    onForceLogout?.();
  };

  if (!isActive || !showWarning) {
    return null;
  }

  return (
    <Modal
      isOpen={showWarning}
      onClose={() => {}} // Prevent closing without action
      title="Session Timeout Warning"
    >
      <div className="space-y-4">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <svg
              className="h-8 w-8 text-yellow-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Your session is about to expire
            </h3>
            <p className="text-sm text-gray-600">
              Your session will expire in approximately{' '}
              <span className="font-semibold text-red-600">
                {timeRemaining} minute{timeRemaining !== 1 ? 's' : ''}
              </span>
              {' '}due to inactivity.
            </p>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
          <p className="text-sm text-yellow-800">
            To protect your account security, we automatically log out inactive sessions.
            Click "Stay Logged In" to extend your session, or "Logout Now" to end your session safely.
          </p>
        </div>

        <div className="flex space-x-3 justify-end">
          <Button
            variant="outline"
            onClick={handleForceLogout}
            className="text-gray-700 border-gray-300 hover:bg-gray-50"
          >
            Logout Now
          </Button>
          <Button
            variant="primary"
            onClick={handleExtendSession}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Stay Logged In
          </Button>
        </div>

        <div className="text-xs text-gray-500 text-center">
          This warning appears {warningThreshold} minutes before automatic logout.
        </div>
      </div>
    </Modal>
  );
};

export default SessionTimeoutWarning;